import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  CheckCircle,
  XCircle,
  Plug,
  ShieldCheck,
  Clock,
  Building2,
} from "lucide-react";
import {
  useGetPairingSessionQuery,
  useApprovePairingMutation,
  useDenyPairingMutation,
} from "@/redux/api/integrations.rtkq";
import { useAuthContext } from "@/hooks/useAuthContext";
import { toast } from "sonner";

/**
 * Public authorize page for partner pairing (Legalix Connect).
 * An external app (עודכנית) opens the browser here with ?user_code=XXXX-XXXX;
 * the signed-in lawyer reviews the request and approves/denies. The partner
 * app polls the token endpoint and completes on its own — the user just
 * returns to it. Structure mirrors InvitationPage (public route + ?redirect=
 * login bounce).
 */

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" dir="rtl">
    <Card className="w-full max-w-md">{children}</Card>
  </div>
);

const StatusCard = ({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body?: string;
  action?: React.ReactNode;
}) => (
  <Shell>
    <CardContent className="flex flex-col items-center py-12 text-center">
      {icon}
      <h2 className="text-lg font-semibold mb-2 mt-4">{title}</h2>
      {body && <p className="text-sm text-muted-foreground whitespace-pre-line">{body}</p>}
      {action && <div className="mt-4 w-full">{action}</div>}
    </CardContent>
  </Shell>
);

const IntegrationsAuthorizePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const userCode = searchParams.get("user_code");
  const { user } = useAuthContext();
  const [manualCode, setManualCode] = useState("");
  const [outcome, setOutcome] = useState<"approved" | "denied" | null>(null);

  const {
    data: session,
    isLoading,
    error,
    refetch,
  } = useGetPairingSessionQuery(userCode!, { skip: !userCode || !user });
  const [approvePairing, { isLoading: approving }] = useApprovePairingMutation();
  const [denyPairing, { isLoading: denying }] = useDenyPairingMutation();

  // ── No code in the URL → manual entry ──
  if (!userCode) {
    return (
      <StatusCard
        icon={<Plug className="h-12 w-12 text-primary" />}
        title="חיבור תוכנה חיצונית"
        body="הזינו את הקוד המוצג בתוכנה (בתבנית XXXX-XXXX)"
        action={
          <div className="flex flex-col gap-3">
            <Input
              dir="ltr"
              className="text-center tracking-widest font-mono"
              placeholder="XXXX-XXXX"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            />
            <Button
              disabled={manualCode.replace(/[^A-Z]/g, "").length < 8}
              onClick={() => setSearchParams({ user_code: manualCode.trim() })}
            >
              המשך
            </Button>
          </div>
        }
      />
    );
  }

  // ── Logged out → bounce through login, preserving the full URL ──
  if (!user) {
    const returnTo = `/integrations/authorize?user_code=${encodeURIComponent(userCode)}`;
    return (
      <StatusCard
        icon={<ShieldCheck className="h-12 w-12 text-primary" />}
        title="נדרשת התחברות"
        body={"תוכנה חיצונית מבקשת להתחבר לחשבון Legalix שלכם.\nכדי לאשר את הבקשה יש להתחבר תחילה."}
        action={
          <Button className="w-full" onClick={() => navigate(`/login?redirect=${encodeURIComponent(returnTo)}`)}>
            התחברות
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Unknown / malformed code ──
  if (error || !session) {
    const status = (error as { status?: number })?.status;
    return (
      <StatusCard
        icon={<XCircle className="h-12 w-12 text-red-400" />}
        title={status === 404 ? "קוד לא מוכר" : "שגיאה בטעינת הבקשה"}
        body={
          status === 404
            ? "הקוד שגוי או שהבקשה כבר אינה פעילה. התחילו חיבור חדש מתוך התוכנה."
            : "נסו לרענן את הדף."
        }
      />
    );
  }

  // ── Local outcome after acting ──
  if (outcome === "approved") {
    return (
      <StatusCard
        icon={<CheckCircle className="h-12 w-12 text-green-500" />}
        title="החיבור אושר"
        body={`אפשר לחזור ל${session.partnerName} — ההתחברות תושלם שם אוטומטית תוך מספר שניות.\nאת החיבור ניתן לנתק בכל עת ממסך האינטגרציות.`}
      />
    );
  }
  if (outcome === "denied") {
    return (
      <StatusCard
        icon={<XCircle className="h-12 w-12 text-red-400" />}
        title="הבקשה נדחתה"
        body="לא נוצר חיבור. אפשר לסגור את החלון."
      />
    );
  }

  // ── Session terminal states ──
  if (session.status === "expired") {
    return (
      <StatusCard
        icon={<Clock className="h-12 w-12 text-amber-500" />}
        title="הבקשה פגה"
        body="חלפו יותר מ־10 דקות. התחילו חיבור חדש מתוך התוכנה."
      />
    );
  }
  if (session.status !== "pending") {
    return (
      <StatusCard
        icon={<CheckCircle className="h-12 w-12 text-muted-foreground" />}
        title="הבקשה כבר טופלה"
        body="קוד זה כבר אושר או נדחה. אם צריך חיבור נוסף — התחילו מחדש מתוך התוכנה."
      />
    );
  }

  // ── Pending: precheck gates ──
  if (!session.precheck.hasOffice) {
    return (
      <StatusCard
        icon={<Building2 className="h-12 w-12 text-amber-500" />}
        title="אין משרד משויך"
        body="כדי לחבר תוכנה חיצונית, החשבון צריך להיות משויך למשרד ב־Legalix. פנו למנהל המשרד שלכם."
      />
    );
  }
  if (!session.precheck.planEntitled) {
    return (
      <StatusCard
        icon={<XCircle className="h-12 w-12 text-amber-500" />}
        title="האינטגרציה אינה כלולה במסלול"
        body={`המסלול הנוכחי של המשרד אינו כולל חיבור ל${session.partnerName}. פנו אלינו לשדרוג המסלול.`}
      />
    );
  }

  const handleApprove = async () => {
    try {
      await approvePairing(userCode).unwrap();
      setOutcome("approved");
    } catch (err) {
      const data = (err as { data?: { code?: string; message?: string } })?.data;
      toast.error(data?.message || "שגיאה באישור החיבור");
      refetch();
    }
  };

  const handleDeny = async () => {
    try {
      await denyPairing(userCode).unwrap();
      setOutcome("denied");
    } catch {
      toast.error("שגיאה בדחיית הבקשה");
      refetch();
    }
  };

  // ── Approve card ──
  return (
    <Shell>
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Plug className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-xl">בקשת חיבור מ{session.partnerName}</CardTitle>
        <CardDescription>
          התוכנה מבקשת הרשאה לפתוח תיקים ולשתף קבצים למשרד שלכם ב־Legalix
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">ודאו שהקוד תואם למוצג בתוכנה</p>
          <p className="text-2xl font-mono tracking-widest" dir="ltr">
            {session.userCode}
          </p>
          {session.deviceName && (
            <p className="text-xs text-muted-foreground mt-2">עמדה: {session.deviceName}</p>
          )}
        </div>

        <div className="bg-blue-50 rounded-lg p-3 text-sm text-center">
          מאשרים בתור <span className="font-medium">{user.email}</span>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          אשרו רק אם אתם אלה שהתחלתם את החיבור מתוך התוכנה. קבצים שישותפו ייכנסו
          למשרד שלכם על שמכם.
        </p>

        <div className="flex gap-3">
          <Button className="flex-1" onClick={handleApprove} disabled={approving || denying}>
            {approving ? (
              <>
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                מאשר...
              </>
            ) : (
              <>
                <CheckCircle className="ml-2 h-4 w-4" />
                אישור החיבור
              </>
            )}
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleDeny}
            disabled={approving || denying}
          >
            דחייה
          </Button>
        </div>
      </CardContent>
    </Shell>
  );
};

export default IntegrationsAuthorizePage;
